import { RestApiClient } from 'ifield-core-components'
import log from 'loglevel'
import _ from 'lodash'
import { FUNCTION_TYPE } from '@/constants'

/**
 * ビル取得
 * @param {Number} id ID
 * @returns {Array.<Object>|Object} データ
 */
const getIndoorBuildings = async (id) => {
  const endPoint = id == null ? 'indoor_building' : `indoor_building/${id}`
  const url = RestApiClient.createApiUrl(endPoint)
  const json = await RestApiClient.executeGetApi(url)
  return json.data
}

/**
 * 初期スロットル間隔(ms)
 */
const INITIAL_THROTTLE_TIME = 30000

/**
 * 当モジュールで扱うデータが利用可能な権限タイプ
 */
const MY_FUNCTION_TYPE = FUNCTION_TYPE.INDOOR

export default {
  namespaced: true,
  state: {
    indoorBuildings: [],
    throttleTime: INITIAL_THROTTLE_TIME,
    throttler: null,
  },
  persistentStates: ['throttleTime'],
  mutations: {
    /**
     * stateを初期化する
     * @param {*} state
     */
    resetState(state) {
      state.indoorBuildings = []
      state.throttleTime = INITIAL_THROTTLE_TIME
      state.throttler = null
    },
    /**
     * ビルを設定する
     * @param {*} state
     * @param {Array.<Object>} indoorBuildings ビル
     */
    setIndoorBuildings(state, indoorBuildings) {
      state.indoorBuildings = indoorBuildings
    },
    /**
     * 特定のビルを設定する
     * @param {*} state
     * @param {Object} indoorBuilding ビル
     */
    setSpecificIndoorBuilding(state, indoorBuilding) {
      const targetIndex = state.indoorBuildings.findIndex(
        (l) => l.id === indoorBuilding.id
      )
      if (targetIndex >= 0) {
        state.indoorBuildings[targetIndex] = indoorBuilding
      } else {
        state.indoorBuildings.push(indoorBuilding)
      }
    },
    /**
     * スロットル間隔を保存する
     * @param {*} state
     * @param {Number} time スロットル間隔(ms)
     */
    setThrottleTime(state, time) {
      state.throttleTime = +time
    },
    /**
     * スロットル関数を保存する
     * @param {*} state
     * @param {Function} throttler スロットル関数
     */
    setThrottler(state, throttler) {
      state.throttler = throttler
    },
  },
  getters: {
    /**
     * ビル
     * @param {*} state
     * @returns {Array.<Object>}
     */
    indoorBuildings: (state) => state.indoorBuildings,
    /**
     * ビル(Map)
     * @param {*} state
     * @returns {Map}
     */
    indoorBuildingMap: (state) =>
      new Map(
        state.indoorBuildings.map((indoorBuilding) => [
          indoorBuilding.id,
          indoorBuilding,
        ])
      ),
    /**
     * 特定IDのビルを取得
     * @param {*} state
     * @param {*} getters
     * @returns {Function} IDを引数に特定IDのビルを返す関数
     */
    getIndoorBuildingById: (state, getters) => (id) =>
      getters.indoorBuildingMap.get(id),
  },
  actions: {
    /**
     * stateをリセットする
     */
    resetState({ commit }) {
      commit('resetState')
    },
    /**
     * IndoorBuildingを更新する
     */
    async updateIndoorBuildings({ commit, rootGetters }) {
      if (!rootGetters['functionType'](MY_FUNCTION_TYPE)) {
        log.info('function type: indoor is unavaliable. skip update')
        return
      }
      const indoorBuildings = await getIndoorBuildings()
      commit('setIndoorBuildings', indoorBuildings)
    },
    /**
     * 特定のIndoorBuildingを更新する
     * @param {Number} id 対象のビルID
     */
    async updateSpecificIndoorBuilding({ commit, rootGetters }, id) {
      if (!rootGetters['functionType'](MY_FUNCTION_TYPE)) {
        log.info('function type: indoor is unavaliable. skip update')
        return
      }
      const indoorBuilding = await getIndoorBuildings(id)
      commit('setSpecificIndoorBuilding', indoorBuilding)
      return indoorBuilding
    },
    /**
     * ビルを更新する(スロットル)
     */
    async updateIndoorBuildingsWithThrottle({ state, commit, dispatch }) {
      if (!state.throttler) {
        const throttler = _.throttle(
          () => dispatch('updateIndoorBuildings'),
          state.throttleTime,
          { trailing: false }
        )
        commit('setThrottler', throttler)
      }
      return state.throttler()
    },
    /**
     * スロットル間隔更新
     * @param {Number} time スロットル間隔(ms)
     */
    updateThrottleTime({ commit }, time) {
      commit('setThrottleTime', +time)
      commit('setThrottler', null)
    },
  },
}
